import api from './auth';
import axios from 'axios';

// Utility function to handle API errors
const handleApiError = (error: any) => {
  if (error.response?.data?.message) {
    return new Error(error.response.data.message);
  }
  return new Error('An unexpected error occurred');
};

// Dashboard
export const getDashboardStats = async () => {
  try {
    const response = await api.get('/admin/dashboard');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get dashboard stats');
  }
};

// Users
export const getAllUsers = async (params: { 
  search?: string;
  role?: string; 
  page?: number; 
  limit?: number;
}) => {
  try {
    const response = await api.get('/admin/users', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get users');
  }
};

export const updateUserRole = async (userId: string, role: string) => {
  try {
    const response = await api.patch(`/admin/users/${userId}/role`, { role });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update user role');
  }
};

export const updateUserActiveStatus = async (userId: string, isActive: boolean) => {
  try {
    const response = await api.patch(`/admin/users/${userId}/active`, { isActive });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update user status');
  }
};

// Properties
export const getAllProperties = async (params: { 
  status?: string; 
  page?: number; 
  limit?: number;
  type?: string;
  search?: string;
} = {}) => {
  try {
    const response = await api.get('/admin/properties', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get properties');
  }
};

export const updatePropertyStatus = async (propertyId: string, status: string) => {
  try {
    const response = await api.patch(`/admin/properties/${propertyId}/status`, { status });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update property status');
  }
};

// Tour interfaces
export interface GetToursParams {
  status?: string;
  propertyId?: string;
  page?: number;
  limit?: number;
  startDate?: string; // Custom param that we'll handle in the request query transformation
  endDate?: string;   // Custom param that we'll handle in the request query transformation
}

export interface UpdateTourStatusParams {
  status: string;
  agentId?: string;
}

// Tour functions
export const getAllTours = async (params: GetToursParams) => {
  try {
    // Extract and transform the params
    const { startDate, endDate, ...apiParams } = params;
    
    // Handle date filtering in the frontend (API doesn't support it directly)
    // We'll pass all params to API but filter results client-side for date ranges
    const response = await api.get('/admin/tours', { params: apiParams });
    let tours = response.data.tours;
    
    // Client-side filtering for date ranges if provided
    if (startDate || endDate) {
      tours = tours.filter((tour: any) => {
        if (!tour.tourDate) return false;
        
        const tourDate = new Date(tour.tourDate);
        
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          // Set end date to end of day for inclusive comparison
          end.setHours(23, 59, 59, 999);
          return tourDate >= start && tourDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          return tourDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          // Set end date to end of day for inclusive comparison 
          end.setHours(23, 59, 59, 999);
          return tourDate <= end;
        }
        
        return true;
      });
    }
    
    return { 
      ...response.data,
      tours
    };
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateTourStatus = async (tourId: string, statusData: string | UpdateTourStatusParams) => {
  try {
    // Handle both string and object input for backwards compatibility
    const payload = typeof statusData === 'string' 
      ? { status: statusData } 
      : statusData;
      
    const response = await api.patch(`/admin/tours/${tourId}/status`, payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Rents (was "Rentals")
export const getAllRents = async (params: { 
  status?: string; 
  page?: number; 
  limit?: number;
  propertyId?: string;
  userId?: string;
} = {}) => {
  try {
    const response = await api.get('/admin/rents', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get rent payments');
  }
};

// Contacts
export interface GetContactsParams {
  search?: string;
  page?: number;
  limit?: number;
}

export const getAllContacts = async (params: GetContactsParams = {}) => {
  try {
    const response = await api.get('/admin/contacts', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getContactById = async (contactId: string) => {
  try {
    const response = await api.get(`/admin/contacts/${contactId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteContact = async (contactId: string) => {
  try {
    const response = await api.delete(`/admin/contacts/${contactId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Agents
export interface AgentData {
  name: string;
  email: string;
  phone: string;
  password?: string;
}

export interface GetAgentsParams {
  search?: string;
  page?: number;
  limit?: number;
}

export const getAllAgents = async (params: GetAgentsParams = {}) => {
  try {
    const response = await api.get('/admin/agents', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getAgentById = async (agentId: string) => {
  try {
    const response = await api.get(`/admin/agents/${agentId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const createAgent = async (agentData: AgentData) => {
  try {
    const response = await api.post('/admin/agents', agentData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateAgent = async (agentId: string, agentData: AgentData) => {
  try {
    const response = await api.put(`/admin/agents/${agentId}`, agentData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteAgent = async (agentId: string) => {
  try {
    const response = await api.delete(`/admin/agents/${agentId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateAgentActiveStatus = async (agentId: string, isActive: boolean) => {
  try {
    const response = await api.patch(`/admin/agents/${agentId}/active`, { isActive });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Property Management Requests
export const getPropertyManagementRequests = async (page = 1, limit = 10) => {
  try {
    const response = await api.get(`/admin/property-management-requests?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Coupon Management
export const getAllCoupons = async ({ search = '', page = 1, limit = 10 }) => {
  try {
    const response = await api.get('/admin/coupons', {
      params: { search, page, limit },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const generateCoupon = async (data: {
  name: string;
  code: string;
  discountPercentage: number;
  maxDiscount: number;
  validUntil: string;
}) => {
  try {
    const response = await api.post('/admin/coupons/generate', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateCoupon = async (id: string, data: {
  name: string;
  code: string;
  discountPercentage: number;
  maxDiscount: number;
  validUntil: string;
}) => {
  try {
    const response = await api.put(`/admin/coupons/${id}`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const toggleCouponStatus = async (id: string, active: boolean) => {
  try {
    const response = await api.patch(`/admin/coupons/${id}/toggle-status`, { active });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteCoupon = async (id: string) => {
  try {
    const response = await api.delete(`/admin/coupons/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}; 